import { Dangerous } from '@mui/icons-material'
import { Box, Grid, Typography } from '@mui/material'
import get from 'lodash/get'
import { useEffect, useMemo, useState } from 'react'
import { useErrorData } from 'src/context/ErrorContext'
import { useFilter } from 'src/context/FilterContext'
import { DefaultService, DelIntelThemeGroupby, DelIntelWorstLocAndItem } from 'src/services/openApiV2'
import { filterNames } from 'src/utils/config/config'
import { formatCurrency } from 'src/utils/functions'
import { pluralize } from 'src/utils/functions/pluralize'
import { ProblematicType } from './ErrorInsightsThemeDrawer'
import ProblematicCard from './ProblematicCard'

interface ErrorInsightsThemeSummaryProps {
  errorTheme: DelIntelThemeGroupby
  activeProblematic: { type: ProblematicType; name: string }
  onProblematicClick: (type: ProblematicType, name: string) => void
}

const ErrorInsightsThemeSummary = ({ errorTheme, activeProblematic, onProblematicClick }: ErrorInsightsThemeSummaryProps) => {
  const { getFilters, getFiltersV2 } = useFilter()
  const { handleError } = useErrorData()

  const [worstPerformingData, setWorstPerformingData] = useState<DelIntelWorstLocAndItem>(null)
  const [worstPerformingDataLoading, setWorstPerformingDataLoading] = useState(true)

  const filterArr = ['b_name', 'vb_name', 'vb_platform', 'am_name', 'chain', 'start_date', 'end_date']

  const filterObj = useMemo(() => {
    return getFiltersV2(filterArr, true)
  }, [...getFilters(filterArr)])

  const getWorstPerformingData = async () => {
    setWorstPerformingDataLoading(true)
    try {
      const res = await DefaultService.callDelIntelDelIntelWorstLocAndItemApiDelIntelDelIntelWorstLocAndItemPost({
        requestBody: {
          ...filterObj,
          theme_in: errorTheme?.theme
        }
      })
      if (res && res[0]) setWorstPerformingData(res[0])
    } catch (err) {
      handleError(err?.message)
    }
    setWorstPerformingDataLoading(false)
  }

  useEffect(() => {
    if (!filterObj) return

    getWorstPerformingData()
  }, [filterObj])

  return (
    <Grid
      container
      spacing={1}
      sx={{ alignItems: 'stretch' }}>
      <Grid
        item
        xs={12}
        sm={4}
        md={3}>
        <Box
          border="1px solid #E6E6E6"
          borderRadius="4px"
          p={2}
          display="flex"
          flexDirection="column"
          gap={1}
          height="100%">
          <Dangerous sx={{ fontSize: '20px', color: '#000' }} />
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}>
            <Typography
              fontSize={12}
              color="#000">
              Error charges
            </Typography>
            <Typography
              fontSize={20}
              color="#E52E2E"
              fontWeight={600}
              height="30px">
              {formatCurrency(get(errorTheme, 'error_charge', 0), { maxFractionDigits: 2 })}
            </Typography>
            <Typography
              fontSize={12}
              color="#0008">
              {get(errorTheme, 'instances', 0)} {pluralize('Error', get(errorTheme, 'instances', 0))}
            </Typography>
          </Box>
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
        md={3}>
        <ProblematicCard
          type={ProblematicType.ITEM}
          title="Most problematic items"
          data={get(worstPerformingData, 'item', []).map((item) => ({
            name: get(item, 'name', ''),
            error_charge: get(item, 'error_charge', 0),
            instances: get(item, 'instances', 0),
            orders: get(item, 'orders', 0)
          }))}
          loading={worstPerformingDataLoading}
          onProblematicClick={onProblematicClick}
          activeProblematic={activeProblematic}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
        md={3}>
        <ProblematicCard
          type={ProblematicType.LOCATION}
          title={`Most problematic ${filterNames.b_name.toLowerCase()}s`}
          data={get(worstPerformingData, 'location', []).map((location) => ({
            name: get(location, 'name', ''),
            error_charge: get(location, 'error_charge', 0),
            instances: get(location, 'instances', 0),
            orders: get(location, 'orders', 0)
          }))}
          loading={worstPerformingDataLoading}
          onProblematicClick={onProblematicClick}
          activeProblematic={activeProblematic}
        />
      </Grid>
    </Grid>
  )
}

export default ErrorInsightsThemeSummary
